"use client";

import { CSSProperties, FC, ReactNode, useState } from "react";
import cn from "classnames";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { Arrow } from "@/icons";
import { VerticalTabPager } from "./VerticalTabPager/VerticalTabPager";
import { VerticalTabPaging } from "./VerticalTab";
import { Button } from "@/components/Button";

export type VerticalTabDesktopProps = {
  variant: "default" | "multiline";
  tabs: {
    label: string;
    head?: string;
    description?: string;
    dlt: string;
    dld: string;
  }[];
  children: ReactNode[];
  theme?: "black" | "gray" | "white";
  tabHeader?: string;
  previousAnalytics?: { dlt: string; dld: string };
  nextAnalytics?: { dlt: string; dld: string };
  nextItem?: {
    displayNextItemLink?: boolean;
    nextItemText: string;
    dlt: string;
    dld: string;
  };
  shownFeatures?: number;
  pagerLabel?: string;
  pagerContainerStyle?: CSSProperties;
  tabPaging: VerticalTabPaging;
  tabClickOverride?: boolean;
};

const VARIANT_MAPPING = {
  default: {
    tabsNavWrapper: "lg:w-94",
    borderColor: "border-swdc-silver-300!",
    label: "text-link-m",
  },
  multiline: {
    tabsNavWrapper: "w-full lg:min-w-[52%]",
    borderColor: "border-swdc-orange-400!",
    label: "text-link-m font-bold",
  },
};

export const VerticalTabDesktop: FC<VerticalTabDesktopProps> = ({
  variant,
  theme,
  tabs,
  children,
  tabHeader,
  previousAnalytics,
  nextAnalytics,
  nextItem,
  shownFeatures = 4,
  pagerLabel,
  pagerContainerStyle,
  tabPaging,
  tabClickOverride,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  if (!tabs?.length) return null;
  const tabItemsLength = tabs.length || 0;
  const isExpanded = tabItemsLength > shownFeatures;

  const nextTabItemIndex = activeTab + (1 % tabItemsLength);

  const setTabs = (toBeSelected: number) => {
    if (toBeSelected === activeTab) return;

    if (toBeSelected === tabPaging.firstTab && toBeSelected !== 0)
      tabPaging.setFirstTab(tabPaging.firstTab - 1);
    else if (
      toBeSelected === tabPaging.lastTab &&
      toBeSelected !== tabItemsLength - 1
    )
      tabPaging.setFirstTab(tabPaging.firstTab + 1);
    else if (toBeSelected === 0 && activeTab === tabItemsLength - 1)
      tabPaging.setFirstTab(0);

    setActiveTab(toBeSelected);
  };

  return (
    <TabGroup
      selectedIndex={activeTab}
      onChange={setTabs}
      vertical
      manual
      className="relative flex w-full"
    >
      <div
        className={cn("flex flex-col", VARIANT_MAPPING[variant].tabsNavWrapper)}
      >
        {tabHeader && (
          <div className="text-subhead-m ui-focus-visible:outline-1 text-headline relative mb-2 text-left font-bold hover:cursor-default focus:outline-0">
            {tabHeader}
          </div>
        )}
        <TabList
          as="ul"
          className="relative hidden w-full flex-col items-start self-start md:flex"
        >
          {tabs?.map((tab, index) => (
            <Tab
              data-linktype={tab.dlt}
              data-linkdetail={tab.dld}
              data-automation-group="tab"
              key={`${tab.label}-${index}`}
              as="li"
              className={({ selected }) =>
                cn(
                  tabPaging.isTabVisible(index) ? "flex" : "hidden",
                  "group ui-focus-visible:outline-swdc-blue-400 relative w-full border border-transparent pr-6 text-left outline-2 -outline-offset-1 outline-transparent hover:cursor-pointer hover:underline data-selected:hover:no-underline",
                  "text-tab-link active:text-button-tertiary-content-pressed",
                  selected &&
                    "bg-background-highlight after:bg-background-highlight w-full border-r-0 after:absolute after:top-0 after:-right-0.5 after:z-10 after:h-full after:w-0.5 after:content-['_']",
                  selected && VARIANT_MAPPING[variant].borderColor,
                )
              }
            >
              {({ selected }) => (
                <div
                  data-automation-id={selected ? "tab-panel-selected" : "tab"}
                  className={cn(
                    "flex w-full flex-row items-center justify-between gap-5 border-b border-transparent pr-3",
                    !selected &&
                      index !== activeTab - 1 &&
                      VARIANT_MAPPING[variant].borderColor,
                  )}
                  onClick={() => tabClickOverride && setTabs(index)}
                >
                  <div className="flex w-full flex-col p-4">
                    <span className={cn(VARIANT_MAPPING[variant].label)}>
                      {tab.label}
                    </span>
                    {variant === "multiline" && (
                      <>
                        {tab.head && (
                          <span className="text-link-m">{tab.head}</span>
                        )}
                        {tab.description && (
                          <span className="text-link-s text-body-secondary group-active:text-button-tertiary-content-pressed">
                            {tab.description}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {!selected && (
                    <Arrow
                      fill=""
                      className="fill-button-tertiary-content-hover group-active:fill-button-tertiary-content-pressed h-3 w-6"
                    />
                  )}
                </div>
              )}
            </Tab>
          ))}
        </TabList>
        {isExpanded ? (
          <div
            className="mt-10 flex justify-end pr-6"
            style={pagerContainerStyle}
          >
            <VerticalTabPager
              title={pagerLabel}
              previousArrow={{
                dld: previousAnalytics?.dld,
                dlt: previousAnalytics?.dlt,
                disabled: activeTab === 0,
                clickHandler: () => setTabs(activeTab - 1),
              }}
              nextArrow={{
                dld: nextAnalytics?.dld,
                dlt: nextAnalytics?.dlt,
                disabled: activeTab === tabItemsLength - 1,
                clickHandler: () => setTabs(activeTab + 1),
              }}
            />
          </div>
        ) : null}
      </div>
      <div
        className={cn(VARIANT_MAPPING[variant].borderColor, "w-full border")}
      >
        <TabPanels
          className={cn(
            VARIANT_MAPPING[variant].borderColor,
            "bg-background-highlight size-full border-solid",
          )}
        >
          {tabs?.map((tab, index) => (
            <TabPanel
              tabIndex={-1}
              unmount={false}
              key={`${tab.label}-${index}`}
              data-automation-group="panel"
            >
              {children?.[index]}
            </TabPanel>
          ))}
          {nextItem?.displayNextItemLink && nextTabItemIndex < tabs.length && (
            <div className="bg-background-highlight px-10 pb-10">
              {nextItem && nextItem.nextItemText && (
                <div className="text-body-secondary pb-2">
                  {nextItem.nextItemText}
                </div>
              )}

              <Button
                variant="tertiary"
                theme={theme}
                icon="none"
                className="text-link-inline-content hover:text-link active:text-link-pressed underline decoration-1 underline-offset-auto hover:decoration-2 hover:underline-offset-1"
                data-automation-group="next-feature"
                onClick={() => {
                  setTabs(nextTabItemIndex);
                }}
                data-linktype={nextItem?.dlt}
                data-linkdetail={nextItem?.dld}
              >
                {tabs &&
                  tabs[nextTabItemIndex >= tabs.length ? 0 : nextTabItemIndex]
                    ?.label}
              </Button>
            </div>
          )}
        </TabPanels>
      </div>
    </TabGroup>
  );
};
